import { useState, useEffect, useCallback, useRef } from "react";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import { fetchUnreadCount } from "../utils/api";

const POLL_INTERVAL = 300000; // 5 minutes

const useUnreadMessages = () => {
  const { user } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef(null);

  const loadUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const data = await fetchUnreadCount(axiosPrivate);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // Silently ignore errors for polling
    }
  }, [user, axiosPrivate]);

  // Start/stop the polling interval
  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // already polling
    intervalRef.current = setInterval(loadUnreadCount, POLL_INTERVAL);
  }, [loadUnreadCount]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      stopPolling();
      return;
    }

    // Initial load
    loadUnreadCount();
    startPolling();

    // Pause polling when tab is hidden, resume when visible
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        loadUnreadCount(); // fresh fetch when user returns
        startPolling();
      }
    };

    // Instant refresh when a push notification arrives (fired by useFirebaseMessaging)
    const handlePushUpdate = () => {
      loadUnreadCount();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("unread-messages-update", handlePushUpdate);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("unread-messages-update", handlePushUpdate);
    };
  }, [user, loadUnreadCount, startPolling, stopPolling]);

  const refresh = useCallback(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  return { unreadCount, refresh };
};

export default useUnreadMessages;
